import React, { useState, useEffect } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import GuestRegisterForm from '../components/novayar/GuestRegisterForm';
import ChatInterface from '../components/novayar/ChatInterface';
import SubscriptionModal from '../components/novayar/SubscriptionModal';
import { isPWA, requestNotificationPermission } from '../utils/pwaDetector';
import { subscribeToPushNotifications } from '../utils/pushNotifications';
import { motion } from 'framer-motion';

const Novayar = () => {
  const { isDarkMode } = useTheme();
  const [userType, setUserType] = useState(null); // 'guest', 'registered', null
  const [userId, setUserId] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [usageStatus, setUsageStatus] = useState(null);
  const [isInPWA, setIsInPWA] = useState(false);

  useEffect(() => {
    // بررسی PWA
    const checkPWA = isPWA();
    setIsInPWA(checkPWA);

    // اگر در PWA هستیم، درخواست permission نوتیفیکیشن
    if (checkPWA) {
      setTimeout(() => {
        requestNotificationPermission().then(granted => {
          if (granted) {
            console.log('✅ Permission نوتیفیکیشن داده شد');
            // ثبت push subscription
            const currentUserId = localStorage.getItem('novayar_guest_id') || null;
            const currentUserType = localStorage.getItem('userToken') ? 'registered' : 'guest';
            subscribeToPushNotifications(currentUserId, currentUserType);
          }
        });
      }, 2000);
    }

    // بررسی وضعیت لاگین کاربر
    const token = localStorage.getItem('userToken');
    const guestId = localStorage.getItem('novayar_guest_id');

    if (token) {
      // کاربر ثبت‌نام شده
      setUserType('registered');
      setIsLoggedIn(true);
      // دریافت mobile از token
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserId(payload.sub);
      } catch (e) {
        console.error('Error parsing token:', e);
      }
    } else if (guestId) {
      // کاربر مهمان
      setUserType('guest');
      setUserId(guestId);
    }
  }, []);

  const handleGuestRegister = (guestId) => {
    localStorage.setItem('novayar_guest_id', guestId);
    setUserType('guest');
    setUserId(guestId);
    
    // ثبت push subscription برای کاربر جدید
    if (isInPWA && Notification.permission === 'granted') {
      subscribeToPushNotifications(guestId, 'guest');
    }
  };

  const handleLimitExceeded = () => {
    setShowSubscriptionModal(true);
  };

  // اگر در نسخه وب هستیم، پیغام نمایش بده
  if (!isInPWA) {
    return (
      <div className={`min-h-screen flex items-center justify-center p-4 transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-blue-950' 
          : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'
      }`}>
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.5 }}
          className={`max-w-2xl w-full text-center rounded-3xl shadow-2xl p-8 sm:p-12 transition-colors duration-300 ${
            isDarkMode 
              ? 'bg-gradient-to-br from-gray-800 to-gray-900 border border-gray-700' 
              : 'bg-white border border-gray-100'
          }`}
        >
          <motion.div 
            animate={{ 
              rotate: [0, 10, -10, 0],
              scale: [1, 1.1, 1]
            }}
            transition={{ 
              duration: 2,
              repeat: Infinity,
              repeatDelay: 3
            }}
            className="text-7xl sm:text-8xl mb-6"
          >
            🤖
          </motion.div>
          
          <h1 className={`text-3xl sm:text-4xl font-black mb-4 transition-colors duration-300 ${
            isDarkMode 
              ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400' 
              : 'text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600'
          }`}>
            نوآیار در اپلیکیشن موجود است! 📱
          </h1>
          
          <p className={`text-lg sm:text-xl mb-8 transition-colors duration-300 ${
            isDarkMode ? 'text-gray-300' : 'text-gray-600'
          }`}>
            برای دسترسی به دستیار هوشمند نوآیار، لطفاً اپلیکیشن نوآفرین‌شو را نصب کنید
          </p>

          <motion.a
            href="/download-app"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            className="inline-block bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white font-bold py-4 px-8 rounded-2xl shadow-xl hover:shadow-2xl transition-all duration-300"
          >
            📥 نصب اپلیکیشن
          </motion.a>
        </motion.div>
      </div>
    );
  }

  // اگر در PWA هستیم، نوآیار را نمایش بده
  return (
    <div className={`min-h-screen flex flex-col transition-colors duration-300 ${
      isDarkMode 
        ? 'bg-gradient-to-br from-gray-900 via-slate-900 to-blue-950' 
        : 'bg-gradient-to-br from-blue-50 via-purple-50 to-pink-50'
    }`}>
      {/* Compact Header */}
      <div className={`transition-colors duration-300 ${
        isDarkMode 
          ? 'bg-gradient-to-r from-blue-900/80 to-purple-900/80 backdrop-blur-md' 
          : 'bg-gradient-to-r from-blue-600/90 to-purple-600/90 backdrop-blur-md'
      } shadow-lg sticky top-0 z-10`}>
        <div className="container mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ 
                rotate: [0, 10, -10, 0]
              }}
              transition={{ 
                duration: 3,
                repeat: Infinity,
                repeatDelay: 5
              }}
              className="text-3xl"
            >
              🤖
            </motion.div>
            <div>
              <h1 className="text-xl sm:text-2xl font-black text-white">نوآیار</h1>
              <p className="text-xs text-white/80 hidden sm:block">دستیار هوشمند نوآفرین‌شو</p>
            </div>
          </div>

          {usageStatus && (
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-2 bg-white/20 backdrop-blur-md rounded-xl px-3 py-2 border border-white/30"
            >
              {usageStatus.has_subscription ? (
                <div className="flex items-center gap-2">
                  <span className="text-2xl">💎</span>
                  <span className="text-sm font-bold text-white hidden sm:block">اشتراک فعال</span>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <div className="relative">
                    <svg className="w-8 h-8" viewBox="0 0 36 36">
                      <circle cx="18" cy="18" r="16" fill="none" stroke="rgba(255,255,255,0.2)" strokeWidth="3"/>
                      <circle 
                        cx="18" 
                        cy="18" 
                        r="16" 
                        fill="none" 
                        stroke="#10b981" 
                        strokeWidth="3"
                        strokeDasharray={`${(usageStatus.remaining / 5) * 100}, 100`}
                        strokeLinecap="round"
                        transform="rotate(-90 18 18)"
                      />
                      <text x="18" y="22" textAnchor="middle" fill="white" fontSize="12" fontWeight="bold">
                        {usageStatus.remaining}
                      </text>
                    </svg>
                  </div>
                  <div className="text-right hidden sm:block">
                    <p className="text-xs text-white/80">پیام باقیمانده</p>
                    <p className="text-xs font-bold text-white">
                      {usageStatus.remaining} از 5
                    </p>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>
      </div>

      {/* Main Content - Chat Area */}
      <div className="flex-1 overflow-hidden flex flex-col">
        {!userType ? (
          // نمایش فرم ثبت نام برای مهمان
          <div className="flex-1 flex items-center justify-center p-4">
            <motion.div 
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="max-w-md w-full"
            >
              <div className={`rounded-2xl sm:rounded-3xl shadow-2xl p-6 sm:p-8 border transition-colors duration-300 ${
                isDarkMode 
                  ? 'bg-gradient-to-br from-gray-800 to-gray-900 border-gray-700' 
                  : 'bg-white border-gray-100'
              }`}>
                <div className="text-center mb-6">
                  <motion.div
                    animate={{ rotate: [0, 10, -10, 0] }}
                    transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
                    className="text-5xl mb-4"
                  >
                    👋
                  </motion.div>
                  <h2 className={`text-2xl font-black mb-3 transition-colors duration-300 ${
                    isDarkMode 
                      ? 'text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-purple-400' 
                      : 'text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-purple-600'
                  }`}>
                    خوش آمدید!
                  </h2>
                  <p className={`text-sm transition-colors duration-300 ${
                    isDarkMode ? 'text-gray-300' : 'text-gray-600'
                  }`}>
                    برای شروع گفتگو با نوآیار، لطفاً اطلاعات خود را وارد کنید
                  </p>
                </div>
                
                <GuestRegisterForm onSuccess={handleGuestRegister} />
                
                <div className={`mt-4 p-3 rounded-xl transition-colors duration-300 ${
                  isDarkMode 
                    ? 'bg-gradient-to-r from-blue-900/30 to-purple-900/30 border border-blue-700/50' 
                    : 'bg-gradient-to-r from-blue-50 to-purple-50 border border-blue-200'
                }`}>
                  <p className={`text-xs text-center transition-colors duration-300 ${
                    isDarkMode ? 'text-blue-300' : 'text-blue-800'
                  }`}>
                    💡 اگر قبلاً در سامانه ثبت‌نام کرده‌اید، می‌توانید{' '}
                    <a 
                      href="/user/login" 
                      className={`font-bold underline transition-colors duration-300 ${
                        isDarkMode 
                          ? 'hover:text-blue-400' 
                          : 'hover:text-blue-600'
                      }`}
                    >
                      وارد شوید
                    </a>
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        ) : (
          // نمایش رابط چت
          <div className="flex-1 overflow-hidden">
            <ChatInterface
              userType={userType}
              userId={userId}
              isLoggedIn={isLoggedIn}
              onLimitExceeded={handleLimitExceeded}
              onUsageStatusChange={setUsageStatus}
            />
          </div>
        )}
      </div>

      {/* Subscription Modal */}
      {showSubscriptionModal && (
        <SubscriptionModal
          userType={userType}
          userId={userId}
          onClose={() => setShowSubscriptionModal(false)}
        />
      )}
    </div>
  );
};

export default Novayar;
